import { Component, inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { Router } from "@angular/router";
import { Store, select } from "@ngrx/store";
import { Observable, Subject, Subscription, of } from "rxjs";
import { map, take, takeUntil } from "rxjs/operators";

import { DropDownDataExpanded, ExternalCrumb, breadCrumbButtonIcon } from "@shared/models/breadcrumbs.interface";
import { PermissionsService } from "@shared/services/permissions/permissions.service";
import { PayGroup } from "@shared/models/pay-groups";
import { ServiceProviderService } from "@shared/services/service-provider/service-provider.service";
import { CustomerEntitySelection } from "@shared/models/customer-entity-selection.interface";
import { Employee, EmployeeReportPayGroupData, PayElectivePayGroupDataCounts } from "@shared/models/employee.interface";
import { SelectOption } from "@shared/models/select-option.interface";
import { getCustomerEntityGroupState, getGlobalDashboardFilterState, getPagination } from "@store/index";
import { AppState } from "@store/models/state.model";
import { PayElectiveApiService } from "../../services/pay-elective-api.service";
import { PayElectiveBankAccountsService } from "../../services/pay-elective-bank-accounts.service";
import { PayElectiveCommonService } from "../../services/pay-elective-common.service";
import { AccountFilter } from "./_models/account-filter.interface";
import { BreadcrumbsComponent } from "@shared/components/breadcrumbs/containers/breadcrumbs.component";
import { PayElectiveListSearchComponent } from "./pay-elective-list-search/pay-elective-list-search.component";
import { PaginationOptions } from "@shared/constants/pagination";
import { PaginationData } from "@store/models/pagination.model";
import { PaginationService } from "@shared/components/pagination/_services/pagination.service";
import { FormControl, FormGroup } from "@angular/forms";
import { IDashboardFiltersFormGroup } from "@modules/global-dashboard/models/global-dashboard-interface";
import { LegalEntityService } from "@shared/services/legal-entity/legal-entity.service";
import { LegalEntity } from "@shared/models/legal-entity.interface";
import { CountriesService } from "@shared/services/countries/countries.service";
import { Country } from "@shared/models/country.interface";
import { ToastService } from "@shared/services/toast/toast.service";
import { MatDialog } from "@angular/material/dialog";
import {
	DeactivateDialogData,
	DeactivateEmployeesConfirmationDialogComponent
} from "./deactivate-employees-confirmation-dialog/deactivate-employees-confirmation-dialog.component";
import { FeatureFlagCodes } from "@shared/constants/feature-flag-codes";
import { FeatureManagementService } from "@shared/services/feature-management/feature-management.service";

@Component({
	selector: "app-pay-elective-master-data",
	templateUrl: "./pay-elective-master-data.component.html",
	styleUrls: ["./pay-elective-master-data.component.scss"]
})
export class PayElectiveMasterDataComponent implements OnInit, OnDestroy {
	@ViewChild(BreadcrumbsComponent) breadCrumbs!: BreadcrumbsComponent;
	@ViewChild(PayElectiveListSearchComponent) payElectiveSearch!: PayElectiveListSearchComponent;

	destroy$ = new Subject<void>();
	employees$: Observable<Employee[]> = new Observable<Employee[]>();
	selectedCustomerId = "";
	selectedLegalEntityId = "";
	selectedPayGroupId = "";
	legalEntityExternalId = "";
	payGroupExternalId = "";
	status = "";
	provider = "";
	providers$: Observable<SelectOption[]> = new Observable<SelectOption[]>();
	payGroup$: Observable<PayGroup> = new Observable<PayGroup>();
	_customerName!: string;
	_payGroupName!: string;
	_country!: string;

	canCreateEmployee = false;
	eburyMigrationEnabled = false;

	// Employee bulk-deactivation selection: id → externalId
	private selectedEmployees = new Map<string, string>();

	/** IDs of selected employees — derived from the map, used by template bindings. */
	get selectedEmployeeIds(): string[] {
		return [...this.selectedEmployees.keys()];
	}

	get allEmployeesSelected(): boolean {
		return this.selectedEmployees.size > 0 && this.selectedEmployees.size === this.totalEmployees;
	}

	providersAccountErrors = new Map<string, { errorCount: number }>();

	//pagination
	pageIndex = 0;
	pageSize = 10;
	totalPageCount!: number;
	counts!: PayElectivePayGroupDataCounts;
	employeeReportCounts!: EmployeeReportPayGroupData;
	totalEmployees = 0;
	employeesDropDownData!: DropDownDataExpanded[];
	isFromGlobalDashboard = false;
	externalCrumbs: ExternalCrumb[] = [];

	//Search and Filter
	searched = "";
	hasBank: boolean | null = null;
	hasCard: boolean | null = null;
	breadCrumbIcon: breadCrumbButtonIcon = {
		name: "add",
		lineWidth: "2.5",
		color: "var(--color-primary-1000)",
		size: "16"
	};
	canSeeMap = false;
	isClientUser = false;
	defaultStatus = "all";
	paginationType: PaginationOptions;

	gdFilters!: FormGroup;
	gdLegalEntityId = "";
	countries: Country[] = [];
	clientLegalEntities: LegalEntity[] = [];
	legalEntitiesSelectOptions: SelectOption[] = [];
	clientCountryList: SelectOption[] = [];
	clientSelectedCountry = "";
	clientSelectedCountryCode = "";
	clientSelectedCountryFlag = "";

	private stateSubscription!: Subscription;

	private payElectiveApiService: PayElectiveApiService = inject(PayElectiveApiService);
	private store: Store<AppState> = inject(Store<AppState>);
	private router: Router = inject(Router);
	private serviceProviderService: ServiceProviderService = inject(ServiceProviderService);
	private permissions: PermissionsService = inject(PermissionsService);
	private payElectiveAccountService: PayElectiveBankAccountsService = inject(PayElectiveBankAccountsService);
	private payElectiveCommonService: PayElectiveCommonService = inject(PayElectiveCommonService);
	private paginationService: PaginationService = inject(PaginationService);
	private legalEntityService: LegalEntityService = inject(LegalEntityService);
	private countriesService: CountriesService = inject(CountriesService);
	private toastService: ToastService = inject(ToastService);
	private matDialog: MatDialog = inject(MatDialog);
	private featureManagementService: FeatureManagementService = inject(FeatureManagementService);

	constructor() {
		this.paginationType = PaginationOptions.EMPLOYEES;

		this.permissions
			.canCreateEmployee()
			.pipe(takeUntil(this.destroy$))
			.subscribe(res => (this.canCreateEmployee = res));

		this.permissions
			.canSeeMapAndIsClient()
			.pipe(takeUntil(this.destroy$))
			.subscribe((res: { isClient: boolean; canSeeMap: boolean }) => {
				this.canSeeMap = res.canSeeMap;
				this.isClientUser = res.isClient;
			});

		this.countriesService
			.getCountries()
			.pipe(take(1))
			.subscribe({
				next: countries => {
					this.countries = countries;
				}
			});
	}

	ngOnInit(): void {
		this.featureManagementService
			.hasFeature(FeatureFlagCodes.EBURY_MIGRATION)
			.pipe(take(1))
			.subscribe((featureEnabled: boolean) => {
				this.eburyMigrationEnabled = featureEnabled;
			});

		this.getPEState();

		this.payElectiveAccountService.reProcessComplete$.pipe(takeUntil(this.destroy$)).subscribe(completed => {
			if (completed) {
				this.getCounts(this.selectedCustomerId, this.selectedLegalEntityId, this.selectedPayGroupId, true);
			}
		});

		this.setupPaginationSubscription();
	}
	resetVeriables() {
		this.provider = "";
		this.selectedEmployees.clear();
	}

	getPEState() {
		this.store.pipe(takeUntil(this.destroy$), select(getCustomerEntityGroupState)).subscribe(state => {
			if (!state) return;

			this.selectedCustomerId = state.customerId;
			this.selectedLegalEntityId = state.legalEntityId;
			this.selectedPayGroupId = state.payGroupId;

			if (this.selectedCustomerId && this.selectedLegalEntityId && this.selectedPayGroupId) {
				this.resetVeriables();
				this.getPayGroup(state.payGroupId);

				// Fetch legal entity external ID for bulk-deactivation payload
				this.legalEntityService
					.getLegalEntity(this.selectedLegalEntityId)
					.pipe(take(1))
					.subscribe(le => {
						this.legalEntityExternalId = le.data.externalId;
					});

				if (this.router.url === "/global-dashboard/master-data") {
					this.getSelectedKPI(state.customerId, state.legalEntityId, state.payGroupId);
					this.isFromGlobalDashboard = true;
				} else {
					this.getCounts(state.customerId, state.legalEntityId, state.payGroupId, true);
					this.isFromGlobalDashboard = false;
				}
			}

			if (this.isClientUser) this.fetchGDFiltersFromState();
		});
	}
	getSelectedKPI(customerId: string, legalEntityId: string, paygroupId: string): void {
		this.store.pipe(takeUntil(this.destroy$), select(getGlobalDashboardFilterState)).subscribe(state => {
			if (state?.globalDashboard?.kpiSelected !== undefined) {
				this.setupPageWithGlobalDashboardState(
					state.globalDashboard.kpiSelected,
					customerId,
					legalEntityId,
					paygroupId
				);
			} else {
				this.router.navigate(["/global-dashboard"]);
			}
		});
	}

	fetchGDFiltersFromState() {
		this.gdFilters = new FormGroup({
			customers: new FormControl([]),
			legalEntities: new FormControl([]),
			paygroups: new FormControl([]),
			statuses: new FormControl([]),
			groups: new FormControl([]),
			deadline: new FormControl(""),
			milestoneStartDate: new FormControl(""),
			milestoneTypes: new FormControl([]),
			process: new FormGroup({
				automatic: new FormControl(false),
				manual: new FormControl(false)
			})
		}) as IDashboardFiltersFormGroup;

		this.store.pipe(takeUntil(this.destroy$), select(getGlobalDashboardFilterState)).subscribe(state => {
			if (state && state.globalDashboard && state.globalDashboard.filters) {
				this.gdFilters.patchValue(state.globalDashboard.filters);

				this.gdLegalEntityId = Array.isArray(this.gdFilters.get("legalEntities")?.value)
					? this.gdFilters.get("legalEntities")?.value[0] || ""
					: this.gdFilters.get("legalEntities")?.value || "";

				this.getClientCountryBreadcrumbInfo();
			}
		});
	}

	getClientCountryBreadcrumbInfo(): void {
		const ALL_OPTION: SelectOption = {
			value: "ALL_OPTIONS",
			text: "All legal entities"
		};
		this.legalEntityService
			.getLegalEntitiesForCustomerAsSelectOption(this.selectedCustomerId, true)
			.pipe(take(1), takeUntil(this.destroy$))
			.subscribe(legalEntities => {
				if (legalEntities.length) {
					this.legalEntitiesSelectOptions = legalEntities;
					this.legalEntitiesSelectOptions.unshift(ALL_OPTION);
				}
			});

		this.legalEntityService
			.getLegalEntitiesForCustomer(this.selectedCustomerId)
			.pipe(take(1))
			.subscribe({
				next: res => {
					this.clientLegalEntities = res.items;
					this.clientCountryList = [];
					this.clientLegalEntities.forEach(legalEntity => {
						if (legalEntity.payGroupCount == 0) return;

						const existingIndex: number = this.clientCountryList.findIndex(
							(country: SelectOption) => country.value === legalEntity.data.country
						);

						if (existingIndex === -1) {
							this.clientCountryList.push(
								this.countriesService.filterCountryByCode(this.countries, legalEntity.data.country)
							);

							this.clientCountryList[this.clientCountryList.length - 1].valueArray = [legalEntity.id];
						} else {
							this.clientCountryList[existingIndex].valueArray?.push(legalEntity.id);
						}
					});

					this.clientSelectedCountry = this.clientCountryList.find(legalEntity => {
						return legalEntity.valueArray?.includes(this.gdLegalEntityId);
					})?.text as "";

					this.clientSelectedCountryCode = this.clientCountryList.find(legalEntity => {
						return legalEntity.valueArray?.includes(this.gdLegalEntityId);
					})?.value as "";

					this.clientSelectedCountryFlag = this.legalEntitiesSelectOptions.filter(
						f => f.value == this.gdLegalEntityId
					)[0]?.imagePath as "";
				}
			});
	}

	setupPageWithGlobalDashboardState(
		kpiSelected: string,
		customerId: string,
		legalEntityId: string,
		paygroupId: string
	): void {
		switch (kpiSelected) {
			case "Failed Beneficiaries":
				{
					this.defaultStatus = "INACTIVE";
					this.status = "INACTIVE";
					this.getCounts(customerId, legalEntityId, paygroupId, true);
					this.externalCrumbs = [
						{ crumb: "Failed Beneficiaries", url: "/global-dashboard" },
						{ crumb: "Employee Data" }
					];
				}
				break;
			case "Missing Cards":
				{
					this.hasCard = false;
					this.getCounts(customerId, legalEntityId, paygroupId, false);
					this.externalCrumbs = [
						{ crumb: "Missing Cards", url: "/global-dashboard" },
						{ crumb: "Employee Data" }
					];
				}
				break;
			case "Missing Bank Acc.":
				{
					this.hasBank = false;
					this.getCounts(customerId, legalEntityId, paygroupId, false);
					this.externalCrumbs = [
						{ crumb: "Missing Bank Acc.", url: "/global-dashboard" },
						{ crumb: "Employee Data" }
					];
				}
				break;
			default:
				this.getCounts(customerId, legalEntityId, paygroupId, true);
		}
	}

	selectionChange(data: CustomerEntitySelection): void {
		this.selectedCustomerId = data.customerId;
		this.selectedLegalEntityId = data.legalEntityId;
		this.selectedPayGroupId = data.payGroupId;
		this.getEmployees();
	}

	getPayGroup(payGroupId: string): void {
		this.payGroup$ = this.payElectiveCommonService.getPayGroupCached(payGroupId);
		this.payGroup$.pipe(take(1)).subscribe(pg => {
			this.payGroupExternalId = pg.externalId ?? "";
		});
	}

	fetchEmployeeData(): void {
		if (this.selectedPayGroupId?.length) {
			this.employees$ = this.payElectiveApiService
				.getEmployeesByPaygroupId(
					this.selectedPayGroupId,
					this.pageSize,
					this.pageIndex,
					this.provider,
					this.status,
					this.searched,
					this.hasBank,
					this.hasCard
				)
				.pipe(
					takeUntil(this.destroy$),
					map(response => {
						this.totalPageCount = response.totalPages;
						this.totalEmployees = response.totalCount;
						this.getEmloyeesListForBreadCrumbDropDown(this.selectedPayGroupId);
						this.setChildZindex(response.items.length);
						return response.items;
					})
				);
		}
	}

	/**
	 * Set z-index of other components from employee list
	 * @param items: number of items displayed on the employee list
	 */
	setChildZindex(items: number): void {
		const zIndex = 5;

		if (this.breadCrumbs) {
			this.breadCrumbs.zIndex = zIndex + items;
		}
		this.payElectiveSearch.zIndex = zIndex + items;
	}

	getEmployees(): void {
		if (!this.selectedPayGroupId) {
			this.employees$ = of([]);
		} else {
			this.fetchEmployeeData();
		}
	}

	getList(searchValue: string): void {
		this.searched = searchValue;
		this.fetchEmployeeData();
	}

	getFilteredList(accFilter: AccountFilter): void {
		this.hasBank = accFilter.bankFilter;
		this.hasCard = accFilter.cardFilter;
		this.fetchEmployeeData();
	}

	getCounts(customerId: string, legalEntityId: string, paygroupId: string, fecthEmployees: boolean) {
		this.payElectiveApiService
			.getEmployeeReport(customerId, paygroupId)
			.pipe(takeUntil(this.destroy$))
			.subscribe({
				next: res => {
					this.employeeReportCounts = res.items[0] ? res.items[0] : ({} as EmployeeReportPayGroupData);
					this.providersAccountErrors.clear();
					this.getTypeDefinitions(legalEntityId, fecthEmployees);
				}
			});
	}

	getTypeDefinitions(entityId: string, fecthEmployees: boolean) {
		this.providers$ = this.serviceProviderService.getProviderNamesByEntityId(entityId).pipe(
			takeUntil(this.destroy$),
			map(_providers => {
				_providers.forEach(provider => {
					this.providersAccountErrors.set(provider.value, {
						errorCount: this.employeeReportCounts.statusCountsByProvider?.[provider.value]?.INACTIVE ?? 0
					});

					this.provider = this.provider === "" ? provider.value : this.provider;
				});

				if (fecthEmployees) this.getEmployees();
				return _providers;
			})
		);
	}

	filtersChanged(searchString: string): void {
		this.status = searchString;
		this.getEmployees();
		this.resetPagination();
	}

	providerChanged(provider: string): void {
		this.provider = provider;
		this.selectedEmployees.clear();
		this.getEmployees();
		this.resetPagination();
	}

	onEmployeeSelectionChanged(event: { id: string; externalId: string; selected: boolean }): void {
		if (event.selected) {
			this.selectedEmployees.set(event.id, event.externalId);
		} else {
			this.selectedEmployees.delete(event.id);
		}
	}

	onSelectAllChanged(selected: boolean): void {
		if (selected) {
			this.payElectiveApiService
				.getEmployeesByPaygroupId(
					this.selectedPayGroupId,
					-1,
					0,
					this.provider,
					this.status,
					this.searched,
					this.hasBank,
					this.hasCard
				)
				.pipe(
					takeUntil(this.destroy$),
					map(response => response.items)
				)
				.subscribe(employees => {
					this.selectedEmployees.clear();
					employees.forEach(e => {
						if (e.id && e.externalId) {
							this.selectedEmployees.set(e.id, e.externalId);
						}
					});
				});
		} else {
			this.selectedEmployees.clear();
		}
	}

	onDeactivateEmployees(): void {
		const selectedExternalIds = [...this.selectedEmployees.values()];
		const count = selectedExternalIds.length;

		const dialogData: DeactivateDialogData = {
			count,
			confirmButtonText: `YES, DEACTIVATE (${count})`,
			backButtonText: "CANCEL",
			confirmationMessage: `You are about to deactivate <strong>${count} employees</strong>.<br>All selected employees will be marked as <strong>Failed</strong> and no<br>payments will be made to them.<br><br>Are you sure you want to continue?`
		};

		const dialogRef = this.matDialog.open(DeactivateEmployeesConfirmationDialogComponent, {
			data: dialogData,
			width: "666px",
			panelClass: "confirm-dialog"
		});

		dialogRef
			.afterClosed()
			.pipe(takeUntil(this.destroy$))
			.subscribe(confirmed => {
				if (!confirmed) return;

				this.payElectiveApiService
					.deactivateEmployeesBulk(selectedExternalIds, this.provider, this.legalEntityExternalId, [
						this.payGroupExternalId
					])
					.pipe(takeUntil(this.destroy$))
					.subscribe({
						next: response => {
							if (Object.keys(response?.errors ?? {}).length > 0) {
								this.toastService.showWarning(
									"Some pay methods cannot be deactivated due to transactions in progress.\nAny pay methods without in-progress transactions will be deactivated as expected."
								);
							} else {
								this.toastService.showSuccess("Employees deactivated successfully");
							}
							this.selectedEmployees.clear();
							this.getCounts(
								this.selectedCustomerId,
								this.selectedLegalEntityId,
								this.selectedPayGroupId,
								true
							);
						},
						error: () => {
							this.toastService.showError("Failed to deactivate employees. Please try again.");
						}
					});
			});
	}

	createPayElective() {
		if (this.isFromGlobalDashboard) {
			this.router.navigate(["/global-dashboard/employee"], {
				state: {
					employee: null,
					customerName: this._customerName,
					employeesDropDownData: this.employeesDropDownData
				}
			});
		} else {
			this.router.navigate(["/employee-data/employee"], {
				state: {
					employee: null,
					customerName: this._customerName,
					employeesDropDownData: this.employeesDropDownData
				}
			});
		}
	}

	getEmloyeesListForBreadCrumbDropDown(payGroupId: string): void {
		if (payGroupId?.length) {
			this.payElectiveApiService
				.getEmployeesByPaygroupId(
					payGroupId,
					-1,
					0,
					this.provider,
					this.status,
					this.searched,
					this.hasBank,
					this.hasCard
				)
				.pipe(
					takeUntil(this.destroy$),
					map(res =>
						res.items.map(
							user =>
								({
									id: user.id,
									name: `${user.data.firstName} ${user.data.lastName}`,
									externalId: user.externalId
								} as DropDownDataExpanded)
						)
					)
				)
				.subscribe({
					next: resultArray => {
						this.employeesDropDownData = resultArray;
					}
				});
		}
	}

	goHome(): void {
		this.router.navigate(["/employee-data"]);
	}

	ngOnDestroy(): void {
		if (this.stateSubscription) {
			this.stateSubscription.unsubscribe();
		}

		this.destroy$.next();
		this.destroy$.complete();
	}

	private setupPaginationSubscription(): void {
		this.store.pipe(select(getPagination), takeUntil(this.destroy$)).subscribe(pagination => {
			if (pagination && pagination.hasOwnProperty("EMPLOYEES")) {
				const employeePagination = pagination["EMPLOYEES"];
				this.pageSize = employeePagination.pageSize;
				this.pageIndex = employeePagination.pageIndex;
				this.fetchEmployeeData();
			}
		});
	}

	private resetPagination(): void {
		const paginationSelection: PaginationData = {
			pageSize: 10,
			pageIndex: 0
		};

		this.paginationService.updatePaginationSate(this.paginationType, paginationSelection);
	}
}
